"use client";

import { MessageSquareQuote, ShieldCheck } from "lucide-react";
import type { KeywordAssignment } from "@super-resume-new/resume-engine";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@super-resume-new/ui/components/accordion";
import { Badge } from "@super-resume-new/ui/components/badge";
import { cn } from "@super-resume-new/ui/lib/utils";

type Derivation = KeywordAssignment["derivation"];

/**
 * How to defend the claim in an interview, by how the claim was derived.
 * `medium` is used when the stretch is wider and a plain restatement is not
 * enough — it asks for a concrete example or an explicit boundary.
 */
const COACHING: Record<Derivation, { base: string; medium?: string }> = {
  stated: {
    base: "Your resume already said this. Answer with the same facts, in your own words, and add the outcome.",
  },
  implied: {
    base: "This follows from work you actually did. Describe what you built first, then point at the part that used this.",
  },
  promoted: {
    base: "You listed this as a skill and the bullet now shows where it was used. Be ready to name the task, the tooling and one problem you hit.",
    medium:
      "Prepare a 60-second walkthrough of one concrete task: what you were asked for, what you wrote, what broke. If your use was small, say so plainly and follow it with what you did do.",
  },
  correlated: {
    base: "You know a sibling technology. Lead with the one you know well, then map the concepts across. Do not claim years you do not have.",
    medium:
      "Say up front which one you used in production and which you have used less. State the boundary yourself — it reads stronger than being caught at it.",
  },
  practice: {
    base: "Answer with the routine, not the tool: how often, who was in the room, what you changed after feedback.",
    medium:
      "Give one concrete instance — a single sprint, review or incident where you did this, and what changed as a result.",
  },
};

const SECTION_LABEL = { experience: "Experience", projects: "Project" } as const;

function claimLocation(ref: KeywordAssignment["bullet_ref"]): string {
  return `${SECTION_LABEL[ref.section]} #${ref.item + 1}, bullet ${ref.bullet + 1}`;
}

export function DefendCards({ assignments }: { assignments: KeywordAssignment[] }) {
  const stretched = assignments
    .filter((a) => a.risk !== "none")
    .sort((a, b) => (a.risk === b.risk ? 0 : a.risk === "medium" ? -1 : 1));

  if (stretched.length === 0) return null;

  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <ShieldCheck size={16} className="text-brand shrink-0" aria-hidden="true" />
        Prepare for these — {stretched.length} {stretched.length === 1 ? "claim" : "claims"} to be
        ready to defend
      </h3>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Each of these rests on something real in your resume, stretched to meet the job description.
        Rehearse the answer once and it holds up.
      </p>

      <Accordion multiple defaultValue={["defend-0"]} className="flex flex-col gap-2">
        {stretched.map((a, i) => {
          const coach = COACHING[a.derivation];
          const medium = a.risk === "medium";
          return (
            <AccordionItem
              key={`${a.keyword}-${i}`}
              value={`defend-${i}`}
              className={cn(
                "rounded-xl border px-3 shadow-sm last:border-b",
                medium ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-card",
              )}
            >
              <AccordionTrigger className="py-3">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-foreground">{a.keyword}</span>
                  <Badge
                    variant={medium ? "warning" : "secondary"}
                    size="sm"
                    className="text-[10px]"
                  >
                    {medium ? "Medium risk" : "Low risk"}
                  </Badge>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    {claimLocation(a.bullet_ref)}
                  </span>
                </span>
              </AccordionTrigger>

              <AccordionPanel className="flex flex-col gap-2.5 pb-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    What it rests on
                  </span>
                  <p className="text-xs text-foreground leading-relaxed">{a.basis}</p>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-brand">
                    <MessageSquareQuote size={12} aria-hidden="true" />
                    How to answer it
                  </span>
                  <p className="text-xs text-foreground leading-relaxed">
                    {(medium && coach.medium) || coach.base}
                  </p>
                </div>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
}
